import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { LANDING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ state: "checking", payment_status: null });

    useEffect(() => {
        if (!sessionId) {
            setStatus({ state: "missing", payment_status: null });
            return;
        }

        let attempts = 0;
        let cancelled = false;
        const maxAttempts = 8;
        const interval = 2000;

        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus({ state: "paid", payment_status: "paid", data });
                    return;
                }
                if (data.status === "expired") {
                    setStatus({ state: "expired", payment_status: data.payment_status });
                    return;
                }
                if (attempts >= maxAttempts) {
                    setStatus({ state: "timeout", payment_status: data.payment_status });
                    return;
                }
                setTimeout(poll, interval);
            } catch (e) {
                if (attempts >= maxAttempts) {
                    setStatus({ state: "error", payment_status: null });
                    return;
                }
                setTimeout(poll, interval);
            }
        };

        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <main className="min-h-screen bg-ivory text-ink flex items-center justify-center px-6">
            <div className="max-w-2xl mx-auto text-center py-24">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink-muted mb-6">
                    Founder Energy Restored
                </p>
                <h1
                    data-testid={LANDING.successTitle}
                    className="font-display text-5xl md:text-7xl font-light leading-none tracking-tighter mb-8"
                >
                    {status.state === "paid" && (
                        <>
                            Welcome
                            <br />
                            <em className="italic font-light text-rust">in.</em>
                        </>
                    )}
                    {status.state === "checking" && "Finalising your seat…"}
                    {status.state === "expired" && "Session expired."}
                    {status.state === "timeout" && "Still processing…"}
                    {status.state === "error" && "Something interrupted us."}
                    {status.state === "missing" && "No session found."}
                </h1>

                <div
                    data-testid={LANDING.successStatus}
                    className="font-sans text-base md:text-lg text-ink-muted leading-relaxed max-w-xl mx-auto"
                >
                    {status.state === "paid" && (
                        <p>
                            Payment received. A confirmation and your access details are on
                            the way to your inbox. The work begins now: sign slower,
                            understand deeper, build bigger.
                        </p>
                    )}
                    {status.state === "checking" && (
                        <p>One moment — we’re confirming with Stripe.</p>
                    )}
                    {status.state === "expired" && (
                        <p>Your checkout session expired before payment completed. Head back and try again.</p>
                    )}
                    {status.state === "timeout" && (
                        <p>
                            We’re still waiting on the final confirmation. If you don’t hear
                            back in a few minutes, please check your email or refresh this
                            page.
                        </p>
                    )}
                    {status.state === "error" && (
                        <p>We couldn’t verify the payment automatically. Please check your inbox or contact support.</p>
                    )}
                    {status.state === "missing" && (
                        <p>This page expects a Stripe session id. Return home to start a new checkout.</p>
                    )}
                </div>

                <div className="mt-14">
                    <Link
                        to="/"
                        data-testid={LANDING.successHome}
                        className="inline-block font-mono text-xs uppercase tracking-[0.2em] border border-ink px-8 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300"
                    >
                        Return home
                    </Link>
                </div>
            </div>
        </main>
    );
}
